var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Group } from "three";
import { CreateSubModelMessage, ModuleId, RemoveSubModelMessage, RenderVisibilityMessage, RoomChangedMessage } from "flavr-common";
import { EndSessionMessage } from "@messages/session/SessionMessages";
import { RoomTransformationUpdateMessage } from "@messages/scenegraph/ScenegraphMessages";
import { MessageHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { inputService } from "@modules/inputs/input/InputService";
import { modelPool } from "@modules/model";
import { mainScene } from "../rendering/mainScene";
/**
 * Renders developer-only wireframe bounding boxes for loaded models so room content and render visibility can be inspected directly in the scene.
 *
 * Messages:
 * - `CreateSubModelMessage` and `RemoveSubModelMessage` - Add and remove tracked wireframe state as models enter or leave the scene graph.
 * - `RoomChangedMessage`, `RoomTransformationUpdateMessage` and `RenderVisibilityMessage` - Keep the debug wireframes aligned with the active room and current render visibility.
 * - `EndSessionMessage` - Clears all cached room and wireframe state when the current session ends.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Registered as a hook for keeping a model's wireframe transform in sync with it, but currently a no-op.
 */
export class DebugWireframeRendererModule extends NewModule {
    /**
     * Creates the root scene node that will hold all per-room wireframe groups and adds it to the main scene.
     */
    constructor() {
        super(ModuleId.DEBUG_WIREFRAME_RENDERER);
        this.rooms = new Map();
        this.wireframeInfos = new Map();
        this.rootNode = new Group();
        this.rootNode.matrixAutoUpdate = false;
        this.rootNode.visible = true;
        mainScene.add(this.rootNode);
    }
    /**
     * Registers a dev-only "ToggleBoundingbox" input action (key B) that toggles the visibility of the wireframe
     * root node and adds or removes it from the main scene accordingly.
     */
    onInit() {
        inputService
            .registerAction("ToggleBoundingbox", () => {
            // switch bounding box
            this.rootNode.visible = !this.rootNode.visible;
            if (this.rootNode.visible) {
                mainScene.add(this.rootNode);
            }
            else {
                mainScene.remove(this.rootNode);
            }
        })
            .addKey("KeyB")
            .asDev();
    }
    /// Handler methods ///
    onReset() {
        this.rooms.clear();
        this.wireframeInfos.clear();
        mainScene.remove(this.rootNode);
        this.rootNode = new Group();
        this.rootNode.matrixAutoUpdate = false;
        this.rootNode.visible = false;
    }
    onModelTransformationUpdate(_model, _localTransform) { }
    onRoomTransformationUpdate(msg) {
        const room = this.getNodeForRoom(msg.uid);
        room.matrix.fromArray(msg.transform);
        room.updateMatrixWorld(true);
    }
    onRoomChangedMessage(message) {
        for (const [uid, room] of this.rooms) {
            if (uid === message.uid) {
                this.rootNode.add(room);
            }
            else {
                this.rootNode.remove(room);
            }
        }
    }
    onCreateSubModel(message, meta) {
        // there is a new model...
        const existing = this.wireframeInfos.get(message.uid);
        if (existing !== undefined) {
            existing.timeStamp = meta.timeStamp;
            return;
        }
        const model = modelPool.getModel(message.uid);
        if (model === undefined || model.isRoot())
            return;
        const parentRoom = model.getRoom();
        if (parentRoom === undefined)
            return;
        this.wireframeInfos.set(message.uid, {
            roomUid: parentRoom.getUID(),
            visible: true,
            timeStamp: meta.timeStamp
        });
    }
    onRemoveSubModelMessage(message, meta) {
        this.removeExistingModel(message.uid, meta.timeStamp);
    }
    onRenderVisibilityMessage(message) {
        const existing = this.wireframeInfos.get(message.uid);
        if (existing) {
            existing.visible = message.visible;
            if (existing.wireframe) {
                existing.wireframe.visible = message.visible;
            }
        }
    }
    /// Private helper methods ///
    getNodeForRoom(roomUid) {
        let room = this.rooms.get(roomUid);
        if (!room) {
            room = new Group();
            room.matrixAutoUpdate = false;
            this.rootNode.add(room);
            this.rooms.set(roomUid, room);
        }
        return room;
    }
    removeExistingModel(uid, timeStamp) {
        const wireframeInfo = this.wireframeInfos.get(uid);
        if (!wireframeInfo || wireframeInfo.timeStamp >= timeStamp)
            return;
        const existingWireframe = wireframeInfo.wireframe;
        if (existingWireframe) {
            this.getNodeForRoom(wireframeInfo.roomUid).remove(existingWireframe);
        }
        this.wireframeInfos.delete(uid);
    }
}
__decorate([
    MessageHandler(EndSessionMessage)
], DebugWireframeRendererModule.prototype, "onReset", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], DebugWireframeRendererModule.prototype, "onModelTransformationUpdate", null);
__decorate([
    MessageHandler(RoomTransformationUpdateMessage)
], DebugWireframeRendererModule.prototype, "onRoomTransformationUpdate", null);
__decorate([
    MessageHandler(RoomChangedMessage)
], DebugWireframeRendererModule.prototype, "onRoomChangedMessage", null);
__decorate([
    MessageHandler(CreateSubModelMessage)
], DebugWireframeRendererModule.prototype, "onCreateSubModel", null);
__decorate([
    MessageHandler(RemoveSubModelMessage)
], DebugWireframeRendererModule.prototype, "onRemoveSubModelMessage", null);
__decorate([
    MessageHandler(RenderVisibilityMessage)
], DebugWireframeRendererModule.prototype, "onRenderVisibilityMessage", null);
